<p-toast role="status" aria-live="polite" aria-atomic="true"></p-toast>
<p-confirmDialog role="alertdialog" aria-modal="true"></p-confirmDialog>

<div class="contract-action-form-container p-6 w-full">
  <!-- Breadcrumb -->
  <p-breadcrumb
    [model]="breadcrumbItems()"
    [home]="{ icon: 'pi pi-home', routerLink: '/backoffice' }"
    class="mb-4"
    [attr.aria-label]="'Percorso di navigazione'"
  ></p-breadcrumb>

  <!-- Loading State -->
  @if (isLoading()) {
    <div
      class="flex align-items-center justify-content-center flex-column"
      style="min-height: 400px"
      role="status"
      aria-live="polite"
    >
      <p-progressSpinner ariaLabel="Caricamento dati azione in corso"></p-progressSpinner>
      <p class="mt-3 text-600">Caricamento in corso...</p>
    </div>
  } @else {
    <!-- Page Header -->
    <div class="surface-0 border-round p-5 mb-4 shadow-1">
      <div class="flex align-items-center justify-content-between flex-wrap gap-3">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-file-edit text-4xl" style="color: #00125b" aria-hidden="true"></i>
          <div>
            <h1 class="text-3xl font-bold m-0 mb-2" style="color: #00125b">{{ pageTitle() }}</h1>
            <p class="m-0 text-600">
              <i class="pi pi-folder mr-2" aria-hidden="true"></i>
              {{ contractName() }}
              @if (contractId()) {
                <span class="ml-2 text-500">({{ contractId() }})</span>
              }
            </p>
          </div>
        </div>

        @if (isEditMode() && action()) {
          <p-tag
            [value]="getActionStatusLabel(action()!.status)"
            [severity]="action()!.status === 'ACTIVE' ? 'success' : 'secondary'"
          ></p-tag>
        }
      </div>
    </div>

    <!-- Form -->
    <form
      class="surface-0 border-round p-5 shadow-1"
      role="form"
      aria-label="Modulo azione contrattuale"
      aria-describedby="contract-action-form-help contract-action-validation-summary"
      novalidate
    >
      <p id="contract-action-form-help" class="text-600 mb-4">
        I campi contrassegnati con <span aria-hidden="true">*</span> sono obbligatori.
      </p>

      <div id="contract-action-validation-summary" class="sr-only" aria-live="polite">
        @if (!canSave()) {
          Completa tutti i campi obbligatori e correggi gli errori evidenziati per abilitare il
          salvataggio.
        } @else {
          Il modulo è valido e può essere salvato.
        }
      </div>

      <!-- Basic Fields Section -->
      <section class="mb-5" aria-labelledby="general-info-heading">
        <h2
          id="general-info-heading"
          class="text-xl font-semibold mb-4 pb-3 border-bottom-1 border-200"
        >
          <i class="pi pi-info-circle mr-2" style="color: #00125b" aria-hidden="true"></i>
          Informazioni Generali
        </h2>

        <div class="form-grid">
          <!-- Action Type -->
          <div class="item">
            <label for="action-type" class="font-semibold block mb-2">
              Tipo Azione <span class="text-red-500" aria-hidden="true">*</span
              ><span class="sr-only">campo obbligatorio</span>
            </label>
            <p-select
              id="action-type"
              name="actionType"
              [(ngModel)]="actionType"
              [options]="actionTypeOptions()"
              optionLabel="label"
              optionValue="value"
              placeholder="Seleziona tipo azione"
              [disabled]="isEditMode()"
              (onChange)="onActionTypeChange()"
              class="w-full"
              ariaLabel="Tipo azione"
              [attr.aria-required]="true"
              [attr.aria-invalid]="!actionType() ? 'true' : 'false'"
              [attr.aria-describedby]="
                !actionType() ? 'action-type-help action-type-error' : 'action-type-help'
              "
            ></p-select>
            <small id="action-type-help" class="text-500 block mt-2">
              @if (isEditMode()) {
                <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
                Il tipo azione non può essere modificato.
              } @else {
                Seleziona il tipo di azione da configurare.
              }
            </small>
            @if (!actionType()) {
              <small id="action-type-error" class="p-error block mt-2" role="alert">
                Seleziona un tipo di azione per proseguire.
              </small>
            }
          </div>

          <!-- Action Name -->
          <div class="item">
            <label for="action-name" class="font-semibold block mb-2">
              Nome Azione <span class="text-red-500" aria-hidden="true">*</span
              ><span class="sr-only">campo obbligatorio</span>
            </label>
            <input
              id="action-name"
              name="actionName"
              type="text"
              pInputText
              [(ngModel)]="actionName"
              placeholder="es. Rinnovo contratto 2026"
              class="w-full"
              [attr.aria-required]="true"
              [attr.aria-invalid]="!actionName().trim() ? 'true' : 'false'"
              [attr.aria-describedby]="
                !actionName().trim() ? 'action-name-help action-name-error' : 'action-name-help'
              "
            />
            <small id="action-name-help" class="text-500 block mt-2">
              Inserisci un nome chiaro e riconoscibile per l'azione contrattuale.
            </small>
            @if (!actionName().trim()) {
              <small id="action-name-error" class="p-error block mt-2" role="alert">
                Inserisci il nome dell'azione.
              </small>
            }
          </div>

          <!-- Description -->
          <div class="item full-width">
            <label for="action-description" class="font-semibold block mb-2"> Descrizione </label>
            <textarea
              id="action-description"
              name="actionNotes"
              pInputTextarea
              [(ngModel)]="actionNotes"
              rows="3"
              placeholder="Inserisci una descrizione dell'azione..."
              class="w-full"
              aria-describedby="action-description-help"
            ></textarea>
            <small id="action-description-help" class="text-500 block mt-2">
              Campo facoltativo: aggiungi dettagli utili per chi consulterà l'azione.
            </small>
          </div>
        </div>
      </section>

      <!-- Type-Specific Fields -->
      @if (actionType()) {
        <section class="mb-5" aria-labelledby="specific-details-heading">
          <h2
            id="specific-details-heading"
            class="text-xl font-semibold mb-4 pb-3 border-bottom-1 border-200"
          >
            <i class="pi pi-cog mr-2" style="color: #00125b" aria-hidden="true"></i>
            Dettagli Specifici - {{ getActionTypeLabel(actionType()!) }}
          </h2>

          <!-- MODIFY_DURATION -->
          @if (actionType() === 'MODIFY_DURATION') {
            <div class="form-grid">
              <div class="item">
                <label for="new-end-date" class="font-semibold block mb-2">
                  Nuova Data di Scadenza <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <p-datepicker
                  id="new-end-date"
                  name="newEndDate"
                  [(ngModel)]="newEndDate"
                  dateFormat="dd/mm/yy"
                  placeholder="Seleziona la nuova data"
                  [showIcon]="true"
                  [minDate]="minEndDate()"
                  class="w-full"
                  ariaLabel="Nuova data di scadenza"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    !newEndDate() ||
                    (currentEndDate() && newEndDate() && newEndDate()! <= currentEndDate()!)
                      ? 'true'
                      : 'false'
                  "
                  [attr.aria-describedby]="
                    !newEndDate() ||
                    (currentEndDate() && newEndDate() && newEndDate()! <= currentEndDate()!)
                      ? 'new-end-date-help new-end-date-error'
                      : 'new-end-date-help'
                  "
                ></p-datepicker>
                <small id="new-end-date-help" class="text-500 block mt-2">
                  @if (currentEndDate()) {
                    <i class="pi pi-info-circle mr-1" aria-hidden="true"></i>
                    Data corrente: <strong>{{ currentEndDate() | date: 'dd/MM/yyyy' }}</strong> - la
                    nuova data deve essere successiva.
                  } @else {
                    Seleziona una data di scadenza futura per il contratto.
                  }
                </small>
                @if (!newEndDate()) {
                  <small id="new-end-date-error" class="p-error block mt-2" role="alert">
                    Seleziona la nuova data di scadenza.
                  </small>
                } @else if (currentEndDate() && newEndDate()! <= currentEndDate()!) {
                  <small id="new-end-date-error" class="p-error block mt-2" role="alert">
                    La nuova data di scadenza deve essere successiva alla data corrente del
                    contratto.
                  </small>
                }
              </div>

              <div
                class="item"
                role="group"
                aria-labelledby="duration-template-label"
                [attr.aria-describedby]="
                  !selectedTemplateId()
                    ? 'duration-template-help duration-template-error'
                    : 'duration-template-help'
                "
              >
                <label id="duration-template-label" class="font-semibold block mb-2">
                  Template Documento <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>

                @if (!selectedTemplate()) {
                  <p-button
                    label="Seleziona dal Registro Template"
                    icon="pi pi-search"
                    [outlined]="true"
                    (onClick)="openTemplateDialog()"
                    styleClass="w-full"
                    ariaLabel="Seleziona template documento"
                    title="Apri dialogo selezione template documento"
                    [attr.aria-describedby]="
                      !selectedTemplateId()
                        ? 'duration-template-help duration-template-error'
                        : 'duration-template-help'
                    "
                  ></p-button>
                } @else {
                  <div
                    class="surface-100 border-round p-4 border-1 border-300"
                    role="status"
                    aria-live="polite"
                  >
                    <div class="flex justify-content-between align-items-start">
                      <div>
                        <h4 class="m-0 mb-2">{{ selectedTemplate()!.label }}</h4>
                      </div>
                      <div class="flex gap-2">
                        <p-button
                          icon="pi pi-pencil"
                          [text]="true"
                          [rounded]="true"
                          (onClick)="openTemplateDialog()"
                          pTooltip="Modifica template"
                          ariaLabel="Modifica template selezionato"
                          title="Modifica template selezionato"
                        ></p-button>
                        <p-button
                          icon="pi pi-times"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          (onClick)="clearTemplate()"
                          pTooltip="Rimuovi template"
                          ariaLabel="Rimuovi template selezionato"
                          title="Rimuovi template selezionato"
                        ></p-button>
                      </div>
                    </div>
                  </div>
                }

                <small id="duration-template-help" class="text-500 block mt-2">
                  Seleziona un template per generare automaticamente l'addendum
                </small>
                @if (!selectedTemplateId()) {
                  <small id="duration-template-error" class="p-error block mt-2" role="alert">
                    Seleziona un template documento per generare l'addendum.
                  </small>
                }
              </div>
            </div>
          }

          <!-- MODIFY_SUBJECT -->
          @if (actionType() === 'MODIFY_SUBJECT') {
            <div class="form-grid">
              <div class="item full-width">
                <label for="new-object" class="font-semibold block mb-2">
                  Nuovo Oggetto del Contratto <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <textarea
                  id="new-object"
                  name="newObject"
                  pInputTextarea
                  [(ngModel)]="newObject"
                  rows="4"
                  placeholder="Descrivi il nuovo oggetto contrattuale"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="!newObject().trim() ? 'true' : 'false'"
                  [attr.aria-describedby]="
                    !newObject().trim() ? 'new-object-help new-object-error' : 'new-object-help'
                  "
                ></textarea>
                <small id="new-object-help" class="text-500 block mt-2">
                  Specifica in dettaglio il nuovo oggetto del contratto
                </small>
                @if (!newObject().trim()) {
                  <small id="new-object-error" class="p-error block mt-2" role="alert">
                    Inserisci il nuovo oggetto del contratto.
                  </small>
                }
              </div>

              <div
                class="item"
                role="group"
                aria-labelledby="object-template-label"
                [attr.aria-describedby]="
                  !selectedTemplateId()
                    ? 'object-template-help object-template-error'
                    : 'object-template-help'
                "
              >
                <label id="object-template-label" class="font-semibold block mb-2">
                  Template Documento <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>

                @if (!selectedTemplate()) {
                  <p-button
                    label="Seleziona dal Registro Template"
                    icon="pi pi-search"
                    [outlined]="true"
                    (onClick)="openTemplateDialog()"
                    styleClass="w-full"
                    ariaLabel="Seleziona template documento"
                    title="Apri dialogo selezione template documento"
                    [attr.aria-describedby]="
                      !selectedTemplateId()
                        ? 'object-template-help object-template-error'
                        : 'object-template-help'
                    "
                  ></p-button>
                } @else {
                  <div
                    class="surface-100 border-round p-4 border-1 border-300"
                    role="status"
                    aria-live="polite"
                  >
                    <div class="flex justify-content-between align-items-start">
                      <div>
                        <h4 class="m-0 mb-2">{{ selectedTemplate()!.label }}</h4>
                      </div>
                      <div class="flex gap-2">
                        <p-button
                          icon="pi pi-pencil"
                          [text]="true"
                          [rounded]="true"
                          (onClick)="openTemplateDialog()"
                          pTooltip="Modifica template"
                          ariaLabel="Modifica template selezionato"
                          title="Modifica template selezionato"
                        ></p-button>
                        <p-button
                          icon="pi pi-times"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          (onClick)="clearTemplate()"
                          pTooltip="Rimuovi template"
                          ariaLabel="Rimuovi template selezionato"
                          title="Rimuovi template selezionato"
                        ></p-button>
                      </div>
                    </div>
                  </div>
                }

                <small id="object-template-help" class="text-500 block mt-2">
                  Seleziona un template per la scrittura integrativa
                </small>
                @if (!selectedTemplateId()) {
                  <small id="object-template-error" class="p-error block mt-2" role="alert">
                    Seleziona un template documento per la modifica dell'oggetto.
                  </small>
                }
              </div>
            </div>
          }

          <!-- MODIFY_ECONOMICS -->
          @if (actionType() === 'MODIFY_ECONOMICS') {
            <div class="form-grid">
              <div class="item">
                <label for="new-currency" class="font-semibold block mb-2">
                  Valuta <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <p-select
                  id="new-currency"
                  name="newCurrency"
                  [(ngModel)]="newCurrency"
                  [options]="currencyOptions()"
                  optionLabel="valueDescription"
                  optionValue="valueKey"
                  placeholder="Seleziona valuta"
                  class="w-full"
                  ariaLabel="Valuta"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="!newCurrency() ? 'true' : 'false'"
                  [attr.aria-describedby]="
                    !newCurrency() ? 'new-currency-help new-currency-error' : 'new-currency-help'
                  "
                ></p-select>
                <small id="new-currency-help" class="text-500 block mt-2">
                  Seleziona la valuta utilizzata
                </small>
                @if (!newCurrency()) {
                  <small id="new-currency-error" class="p-error block mt-2" role="alert">
                    Seleziona la valuta utilizzata per i nuovi importi.
                  </small>
                }
              </div>

              <div class="item">
                <label for="total-contract-value" class="font-semibold block mb-2">
                  Valore Durata Contratto IVA Esclusa
                  <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <p-input-number
                  id="total-contract-value"
                  name="newTotalContractValue"
                  [(ngModel)]="newTotalContractValue"
                  mode="currency"
                  [currency]="newCurrency() || 'EUR'"
                  currencyDisplay="symbol"
                  locale="it-IT"
                  placeholder="0,00"
                  class="w-full"
                  ariaLabel="Valore durata contratto IVA esclusa"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    newTotalContractValue() === undefined ||
                    newTotalContractValue() === null ||
                    newTotalContractValue()! < 0
                      ? 'true'
                      : 'false'
                  "
                  [attr.aria-describedby]="
                    newTotalContractValue() === undefined ||
                    newTotalContractValue() === null ||
                    newTotalContractValue()! < 0
                      ? 'total-contract-value-help total-contract-value-error'
                      : 'total-contract-value-help'
                  "
                ></p-input-number>
                <small id="total-contract-value-help" class="text-500 block mt-2">
                  Indica il valore totale della durata del contratto
                </small>
                @if (
                  newTotalContractValue() === undefined ||
                  newTotalContractValue() === null ||
                  newTotalContractValue()! < 0
                ) {
                  <small id="total-contract-value-error" class="p-error block mt-2" role="alert">
                    Inserisci un valore totale pari o superiore a 0.
                  </small>
                }
              </div>

              <div class="item">
                <label for="annual-value" class="font-semibold block mb-2">
                  Valore Annuale IVA Esclusa <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <p-input-number
                  id="annual-value"
                  name="newAnnualValue"
                  [(ngModel)]="newAnnualValue"
                  mode="currency"
                  [currency]="newCurrency() || 'EUR'"
                  currencyDisplay="symbol"
                  locale="it-IT"
                  placeholder="0,00"
                  class="w-full"
                  ariaLabel="Valore annuale IVA esclusa"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    newAnnualValue() === undefined ||
                    newAnnualValue() === null ||
                    newAnnualValue()! < 0
                      ? 'true'
                      : 'false'
                  "
                  [attr.aria-describedby]="
                    newAnnualValue() === undefined ||
                    newAnnualValue() === null ||
                    newAnnualValue()! < 0
                      ? 'annual-value-help annual-value-error'
                      : 'annual-value-help'
                  "
                ></p-input-number>
                <small id="annual-value-help" class="text-500 block mt-2">
                  Indica il valore annuale del contratto
                </small>
                @if (
                  newAnnualValue() === undefined ||
                  newAnnualValue() === null ||
                  newAnnualValue()! < 0
                ) {
                  <small id="annual-value-error" class="p-error block mt-2" role="alert">
                    Inserisci un valore annuale pari o superiore a 0.
                  </small>
                }
              </div>

              <div
                class="item"
                role="group"
                aria-labelledby="fees-template-label"
                [attr.aria-describedby]="
                  !selectedTemplateId()
                    ? 'fees-template-help fees-template-error'
                    : 'fees-template-help'
                "
              >
                <label id="fees-template-label" class="font-semibold block mb-2">
                  Template Documento <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>

                @if (!selectedTemplate()) {
                  <p-button
                    label="Seleziona dal Registro Template"
                    icon="pi pi-search"
                    [outlined]="true"
                    (onClick)="openTemplateDialog()"
                    styleClass="w-full"
                    ariaLabel="Seleziona template documento"
                    title="Apri dialogo selezione template documento"
                    [attr.aria-describedby]="
                      !selectedTemplateId()
                        ? 'fees-template-help fees-template-error'
                        : 'fees-template-help'
                    "
                  ></p-button>
                } @else {
                  <div
                    class="surface-100 border-round p-4 border-1 border-300"
                    role="status"
                    aria-live="polite"
                  >
                    <div class="flex justify-content-between align-items-start">
                      <div>
                        <h4 class="m-0 mb-2">{{ selectedTemplate()!.label }}</h4>
                      </div>
                      <div class="flex gap-2">
                        <p-button
                          icon="pi pi-pencil"
                          [text]="true"
                          [rounded]="true"
                          (onClick)="openTemplateDialog()"
                          pTooltip="Modifica template"
                          ariaLabel="Modifica template selezionato"
                          title="Modifica template selezionato"
                        ></p-button>
                        <p-button
                          icon="pi pi-times"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          (onClick)="clearTemplate()"
                          pTooltip="Rimuovi template"
                          ariaLabel="Rimuovi template selezionato"
                          title="Rimuovi template selezionato"
                        ></p-button>
                      </div>
                    </div>
                  </div>
                }

                <small id="fees-template-help" class="text-500 block mt-2">
                  Seleziona un template per l'addendum economico
                </small>
                @if (!selectedTemplateId()) {
                  <small id="fees-template-error" class="p-error block mt-2" role="alert">
                    Seleziona un template documento per l'addendum economico.
                  </small>
                }
              </div>
            </div>
          }

          <!-- EXTEND_PARTIES -->
          @if (actionType() === 'EXTEND_PARTIES') {
            <div class="form-grid">
              <div class="item full-width">
                <label class="font-semibold block mb-2">
                  Entity Coinvolte <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <div class="flex flex-column gap-3">
                  @for (entity of newEntities(); track $index) {
                    <div class="surface-50 border-round p-4">
                      <div class="flex justify-content-between align-items-center mb-3">
                        <h4 class="text-md font-semibold m-0">Entity Coinvolta #{{ $index + 1 }}</h4>
                        <p-button
                          icon="pi pi-trash"
                          severity="danger"
                          size="small"
                          [text]="true"
                          (onClick)="removeInvolvedEntity($index)"
                          [disabled]="newEntities().length === 1"
                        ></p-button>
                      </div>

                      <div class="grid">
                        <div class="field col-12 md:col-4">
                          <label [for]="'new-entity-code-' + $index">Denominazione Entità</label>
                          <p-select
                            [inputId]="'new-entity-code-' + $index"
                            [(ngModel)]="newEntities()[$index].entityCode"
                            [ngModelOptions]="{ standalone: true }"
                            [options]="getAvailableInvolvedEntityOptions($index)"
                            optionLabel="entityDescription"
                            optionValue="entityCode"
                            placeholder="Seleziona entità"
                            (onChange)="onInvolvedEntityChange($index)"
                            class="w-full"
                          ></p-select>
                        </div>

                        <div class="field col-12 md:col-4">
                          <label [for]="'new-signatory-name-' + $index">
                            Firmato da ({{ getInvolvedEntityLabel(entity.entityCode) }})
                          </label>
                          <input
                            [id]="'new-signatory-name-' + $index"
                            pInputText
                            [(ngModel)]="newEntities()[$index].signatoryName"
                            [ngModelOptions]="{ standalone: true }"
                            class="w-full"
                          />
                        </div>

                        <div class="field col-12 md:col-4">
                          <label [for]="'new-signatory-role-' + $index">
                            Ruolo Firmatario ({{ getInvolvedEntityLabel(entity.entityCode) }})
                          </label>
                          <input
                            [id]="'new-signatory-role-' + $index"
                            pInputText
                            [(ngModel)]="newEntities()[$index].signatoryRole"
                            [ngModelOptions]="{ standalone: true }"
                            class="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  }

                  <div class="flex justify-content-end">
                    <p-button
                      label="Aggiungi Entity Coinvolta"
                      icon="pi pi-plus"
                      size="small"
                      (onClick)="addInvolvedEntity()"
                      [disabled]="!canAddMoreInvolvedEntities()"
                    ></p-button>
                  </div>
                </div>
                <small id="new-entities-help" class="text-500 block mt-2">
                  Seleziona almeno una entity e compila, se necessario, i dati del firmatario.
                </small>
                @if (!hasValidExtendPartiesSelection()) {
                  <small id="new-entities-error" class="p-error block mt-2" role="alert">
                    Inserisci almeno una entity valida senza duplicazioni.
                  </small>
                }
              </div>

              <div
                class="item"
                role="group"
                aria-labelledby="parties-template-label"
                [attr.aria-describedby]="
                  !selectedTemplateId()
                    ? 'parties-template-help parties-template-error'
                    : 'parties-template-help'
                "
              >
                <label id="parties-template-label" class="font-semibold block mb-2">
                  Template Documento <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>

                @if (!selectedTemplate()) {
                  <p-button
                    label="Seleziona dal Registro Template"
                    icon="pi pi-search"
                    [outlined]="true"
                    (onClick)="openTemplateDialog()"
                    styleClass="w-full"
                    ariaLabel="Seleziona template documento"
                    title="Apri dialogo selezione template documento"
                    [attr.aria-describedby]="
                      !selectedTemplateId()
                        ? 'parties-template-help parties-template-error'
                        : 'parties-template-help'
                    "
                  ></p-button>
                } @else {
                  <div
                    class="surface-100 border-round p-4 border-1 border-300"
                    role="status"
                    aria-live="polite"
                  >
                    <div class="flex justify-content-between align-items-start">
                      <div>
                        <h4 class="m-0 mb-2">{{ selectedTemplate()!.label }}</h4>
                      </div>
                      <div class="flex gap-2">
                        <p-button
                          icon="pi pi-pencil"
                          [text]="true"
                          [rounded]="true"
                          (onClick)="openTemplateDialog()"
                          pTooltip="Modifica template"
                          ariaLabel="Modifica template selezionato"
                          title="Modifica template selezionato"
                        ></p-button>
                        <p-button
                          icon="pi pi-times"
                          [text]="true"
                          [rounded]="true"
                          severity="danger"
                          (onClick)="clearTemplate()"
                          pTooltip="Rimuovi template"
                          ariaLabel="Rimuovi template selezionato"
                          title="Rimuovi template selezionato"
                        ></p-button>
                      </div>
                    </div>
                  </div>
                }

                <small id="parties-template-help" class="text-500 block mt-2">
                  Seleziona un template per formalizzare l'estensione delle parti
                </small>
                @if (!selectedTemplateId()) {
                  <small id="parties-template-error" class="p-error block mt-2" role="alert">
                    Seleziona un template documento per formalizzare l'estensione delle parti.
                  </small>
                }
              </div>
            </div>
          }

          <!-- RENEW_CONTRACT -->
          @if (actionType() === 'RENEW_CONTRACT') {
            <div class="form-grid">
              <div class="item full-width">
                <div
                  class="p-4 bg-yellow-50 border-1 border-yellow-200 border-round mb-3"
                  role="status"
                  aria-label="Informazioni procedura di rinnovo"
                >
                  <div class="flex align-items-start gap-2">
                    <i
                      class="pi pi-exclamation-triangle text-yellow-600 text-xl"
                      aria-hidden="true"
                    ></i>
                    <div>
                      <p class="m-0 mb-2 font-semibold text-yellow-900">Procedura di Rinnovo</p>
                      <ul class="m-0 pl-3 text-sm text-yellow-900">
                        <li>Il contratto passerà in stato "Rinnovo in Corso"</li>
                        <li>
                          Verrà creato un nuovo contratto in stato "Bozza" con i dati del contratto
                          corrente
                        </li>
                        <li>
                          Al completamento del rinnovo, il vecchio contratto passerà a "Scaduto"
                        </li>
                        <li>
                          Il nuovo contratto dovrà essere finalizzato e attivato separatamente
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div class="item full-width">
                <label for="renewal-notes" class="font-semibold block mb-2">
                  Note sul Rinnovo
                </label>
                <textarea
                  id="renewal-notes"
                  name="renewalNotes"
                  pInputTextarea
                  [(ngModel)]="renewalNotes"
                  rows="4"
                  placeholder="Inserisci eventuali note o modifiche previste per il rinnovo"
                  class="w-full"
                  aria-describedby="renewal-notes-help"
                ></textarea>
                <small id="renewal-notes-help" class="text-500 block mt-2">
                  Indica eventuali modifiche o annotazioni relative al rinnovo
                </small>
              </div>
            </div>
          }

          <!-- TERMINATION -->
          @if (actionType() === 'TERMINATION') {
            <div class="form-grid">
              <div class="item full-width">
                <div
                  class="p-4 bg-red-50 border-1 border-red-200 border-round mb-3"
                  role="alert"
                  aria-label="Avviso procedura di recesso"
                >
                  <div class="flex align-items-start gap-2">
                    <i class="pi pi-exclamation-circle text-red-600 text-xl" aria-hidden="true"></i>
                    <div>
                      <p class="m-0 mb-2 font-semibold text-red-900">
                        Attenzione: Procedura di Recesso
                      </p>
                      <p class="m-0 text-sm text-red-900">
                        Una volta completata questa azione, il contratto passerà in stato "Cessato"
                        e non potrà più essere modificato. Assicurati di aver completato tutte le
                        procedure necessarie prima di procedere.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="item full-width">
                <label for="termination-reason" class="font-semibold block mb-2">
                  Motivazioni del Recesso <span class="text-red-500" aria-hidden="true">*</span
                  ><span class="sr-only">campo obbligatorio</span>
                </label>
                <textarea
                  id="termination-reason"
                  name="terminationReason"
                  pInputTextarea
                  [(ngModel)]="terminationReason"
                  rows="5"
                  placeholder="Indicare in dettaglio le motivazioni del recesso dal contratto"
                  class="w-full"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="!terminationReason().trim() ? 'true' : 'false'"
                  [attr.aria-describedby]="
                    !terminationReason().trim()
                      ? 'termination-reason-help termination-reason-error'
                      : 'termination-reason-help'
                  "
                ></textarea>
                <small id="termination-reason-help" class="text-500 block mt-2">
                  Le motivazioni devono essere chiare e dettagliate per motivi legali
                </small>
                @if (!terminationReason().trim()) {
                  <small id="termination-reason-error" class="p-error block mt-2" role="alert">
                    Inserisci le motivazioni del recesso in modo chiaro e dettagliato.
                  </small>
                }
              </div>

              <div class="item">
                <label for="termination-template" class="font-semibold block mb-2">
                  Template Lettera di Recesso (Opzionale)
                </label>
                <p-select
                  id="termination-template"
                  name="selectedTemplateId"
                  [(ngModel)]="selectedTemplateId"
                  [options]="templateOptions()"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleziona template lettera di recesso"
                  class="w-full"
                  [showClear]="true"
                  ariaLabel="Template lettera di recesso"
                  aria-describedby="termination-template-help"
                ></p-select>
                <small id="termination-template-help" class="text-500 block mt-2">
                  Seleziona un template per generare la lettera di recesso
                </small>
              </div>
            </div>
          }
        </section>
      }

      <!-- Action Buttons -->
      <div class="flex justify-content-end gap-3 pt-4 border-top-1 border-200">
        <p-button
          label="Annulla"
          icon="pi pi-times"
          [outlined]="true"
          size="large"
          (onClick)="cancel()"
          [disabled]="isSaving()"
          ariaLabel="Annulla modifica azione"
          title="Annulla modifica azione"
        ></p-button>
        <p-button
          [label]="isEditMode() ? 'Salva Modifiche' : 'Crea Azione'"
          icon="pi pi-check"
          size="large"
          (onClick)="save()"
          [disabled]="!canSave()"
          [loading]="isSaving()"
          [ariaLabel]="isEditMode() ? 'Salva modifiche azione' : 'Crea nuova azione'"
          [title]="isEditMode() ? 'Salva modifiche azione' : 'Crea nuova azione'"
          [attr.aria-describedby]="!canSave() ? 'contract-action-validation-summary' : null"
        ></p-button>
      </div>
    </form>
  }
</div>

<!-- Template Selection Dialog -->
<p-dialog
  [(visible)]="showTemplateDialog"
  [modal]="true"
  [style]="{ width: '90vw', maxWidth: '1200px' }"
  [dismissableMask]="true"
  [draggable]="false"
  [resizable]="false"
  [closeOnEscape]="true"
  aria-labelledby="template-dialog-title"
  ariaLabel="Selezione template documento"
  role="dialog"
>
  <ng-template pTemplate="header">
    <span class="text-xl font-semibold" id="template-dialog-title"
      >Seleziona Template Documento</span
    >
  </ng-template>

  <div class="p-4" role="region" aria-labelledby="template-dialog-title">
    <app-registry-table
      [columns]="templateColumns"
      [data]="templateTableData()"
      (onSelectClick)="selectTemplate($event)"
    ></app-registry-table>
  </div>
</p-dialog>
